import ChangePasswordBlock from '@anm/components/ChangePasswordBlock';
import DeleteAccountBlock from '@anm/components/DeleteAccountBlock';
import TitleBlock from '@anm/components/TitleBlock';
import UserForm, { UserFormFields } from '@anm/components/user/UserForm';
import noop from '@anm/helpers/noop';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import useChangePasswordHandler from '../../../hooks/user/useChangePasswordHandler';
import useDeleteAccountHandler from '../../../hooks/user/useDeleteAccountHandler';
import useResendConfirmEmailHandler from '../../../hooks/user/useResendConfirmEmailHandler';
import { userSelectors } from '../../../store/modules/user';

import UserProfileWrapper, { UserFormWrapper } from './Wrapper';
import { useAutoSave } from './hooks';

export { UserProfileWrapper };

type UserProfileProps = {
  title?: string | null;
};

const UserProfile: FC<UserProfileProps> = ({ title = 'Profile' }) => {
  const deleteAccount = useDeleteAccountHandler();

  const profile = useSelector(userSelectors.selectUserProfile);
  const userTeamId = useSelector(userSelectors.selectTeamId);
  const userTeamRole = useSelector(userSelectors.selectTeamRole);
  const profileError = useSelector(userSelectors.selectUserError);
  const updateUserParams = useSelector(userSelectors.selectUpdateUserParams);
  const isProfilePending = useSelector(userSelectors.selectUserPending);
  const isResendEmailPending = useSelector(userSelectors.selectResendEmailPending);
  const handleResendConfirmEmail = useResendConfirmEmailHandler();
  const { handleAutoSave } = useAutoSave();

  const changePasswordError = useSelector(userSelectors.selectChangePasswordError);
  const isChangePasswordPending = useSelector(userSelectors.selectChangePasswordPending);
  const handleChangePassword = useChangePasswordHandler();

  if (!profile) return null;

  const { avatar, email, username, displayName } = profile;

  const validateForm = ({ username }: UserFormFields) => {
    const usernameError = profileError && new RegExp(/username/gi).test(profileError.message) && profileError.message;

    return { username: (username === updateUserParams?.username && usernameError) || undefined };
  };

  return (
    <UserProfileWrapper>
      {title && <TitleBlock size="super-medium" {...{ title }} />}
      <UserFormWrapper>
        <UserForm
          initialValues={{
            email,
            avatar,
            username,
            displayName
          }}
          disabledFields={{ email: !!userTeamId && userTeamRole !== 'OWNER' }}
          isPending={isProfilePending}
          isResendEmailPending={isResendEmailPending}
          hasEmailWarning={!profile.emailConfirmed}
          onSubmit={noop}
          onAutoSave={handleAutoSave}
          onEmailResend={handleResendConfirmEmail}
          validate={validateForm}
        />
      </UserFormWrapper>
      <ChangePasswordBlock
        onSubmit={handleChangePassword}
        isPending={isChangePasswordPending}
        submitError={changePasswordError}
      />
      <DeleteAccountBlock onDelete={deleteAccount} />
    </UserProfileWrapper>
  );
};

export default UserProfile;
